import React from 'react'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { FormField } from '@/components/forms/FormField'
import { FormFieldGrid } from '@/components/forms/FormFieldGrid'
import { TextInput } from '@/components/forms/TextInput'

import { ArrayLevelError, RemoveRowButton, RowError, SourceInputs } from './SourceRow'

/**
 * A list of named values: rows of `{ [keyName]: ..., [valueName]: ... }`.
 *
 * Backs TCN's call metadata and the generic connector's. Same row bookkeeping as
 * TokenListFields, but each row carries a name alongside the source.
 *
 * Column budget: FormFieldGrid is 12 spans wide plus a fixed column for the trash
 * button, so keySpan + sourceSpan + pathSpan must come to 12 -- and to 12 in BOTH
 * modes, because SourceInputs collapses to `sourceSpan + pathSpan` when there is no
 * source select. The defaults below satisfy both (3 + 4 + 5, and 3 + 9). Overflow
 * does not clip, it wraps: the path lands on a second grid line under the name
 * column while its header stays put, which is what shipped when these spans were
 * accepted here and never forwarded to the row.
 *
 * The header spans are derived from the same three props for that reason -- a header
 * that hardcodes its own widths is free to drift from the row it labels.
 */
export const KeyedTokenFields = ({
  field,
  label,
  codec,
  keyName = 'key',
  valueName = 'source',
  sources,
  examples,
  isWholeValue,
  placeholder,
  help,
  caption,
  keyLabel = 'Name',
  keyPlaceholder,
  keySpan = 3,
  sourceSpan = 4,
  pathSpan = 5,
  columnLabels = ['Path'],
  addLabel = 'Add field',
  removeLabel = 'Remove field',
  testIdPrefix,
  containerTestId,
}) => {
  const { values, errors, setFieldValue } = useFormikContext()

  // A hand-edited row arrives unvalidated; a non-object here would throw on
  // row[keyName]. The resolver guards the same way before building the map.
  //
  // Stored indices are preserved for the same reason as TokenListFields: Yup
  // validates the unfiltered array, so errors[field] is indexed against it.
  const rows = (Array.isArray(values[field]) ? values[field] : [])
    .map((row, storedIndex) => ({ row, storedIndex }))
    .filter(({ row }) => row && typeof row === 'object')

  const storedRows = rows.map(({ row }) => row)

  const setRow = (index, patch) =>
    setFieldValue(
      field,
      storedRows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    )
  const addRow = () => setFieldValue(field, [...storedRows, { [keyName]: '', [valueName]: '' }])
  const removeRow = (index) =>
    setFieldValue(
      field,
      storedRows.filter((_, rowIndex) => rowIndex !== index)
    )

  const rowError = (storedIndex, name) => errors[field]?.[storedIndex]?.[name]
  const hasSources = !isEmpty(sources)

  return (
    <FormField data-testid={containerTestId || `${testIdPrefix}-fields`}>
      <label>{label}</label>
      {help}
      {caption && <div className="form-caption">{caption}</div>}
      {!isEmpty(rows) && (
        <FormFieldGrid>
          <FormField required span={keySpan}>
            <label>{keyLabel}</label>
          </FormField>
          {hasSources && (
            <FormField span={sourceSpan}>
              <label>{columnLabels[0]}</label>
            </FormField>
          )}
          {/* Mirrors SourceInputs: with no select, the path takes both spans. */}
          <FormField span={hasSources ? pathSpan : sourceSpan + pathSpan}>
            <label>{hasSources ? columnLabels[1] : columnLabels[0]}</label>
          </FormField>
        </FormFieldGrid>
      )}
      {rows.map(({ row, storedIndex }, index) => {
        const { prefix, path } = codec.split(row[valueName])
        return (
          // eslint-disable-next-line react/no-array-index-key
          <FormFieldGrid key={index}>
            <FormField span={keySpan}>
              <TextInput
                value={row[keyName] || ''}
                placeholder={keyPlaceholder}
                data-testid={`${testIdPrefix}-key-${index}`}
                aria-label={`${keyLabel} ${index + 1}`}
                onChange={(event) => setRow(index, { [keyName]: event.target.value })}
              />
              <RowError message={rowError(storedIndex, keyName)} />
            </FormField>
            <SourceInputs
              pathLabel={hasSources ? columnLabels[1] : columnLabels[0]}
              sources={sources}
              examples={examples}
              isWholeValue={isWholeValue}
              placeholder={placeholder}
              sourceSpan={sourceSpan}
              pathSpan={pathSpan}
              prefix={prefix}
              path={path}
              index={index}
              testIdPrefix={testIdPrefix}
              onChange={({ prefix: nextPrefix, path: nextPath }) =>
                setRow(index, { [valueName]: codec.join(nextPrefix, nextPath) })
              }
            />
            <RemoveRowButton
              label={removeLabel}
              testId={`${testIdPrefix}-remove-${index}`}
              onClick={() => removeRow(index)}
            />
            <RowError message={rowError(storedIndex, valueName)} />
          </FormFieldGrid>
        )
      })}
      <Button type="button" onClick={addRow} data-testid={`${testIdPrefix}-add`}>
        {addLabel}
      </Button>
      <ArrayLevelError error={errors[field]} />
    </FormField>
  )
}
